import { useLayoutEffect, useRef } from 'react'

/**
 * One overlay open at a time: opening a menu, sheet, popover or palette closes
 * every other one that is open.
 *
 * Each of these used to close itself only on a `mousedown` outside it, and none
 * knew about the others. So whatever opened a second overlay without a mousedown
 * (a keyboard or assistive-tech activation, a programmatic open) left the first
 * one open, and a menu's z-index (200) put it above the add sheet's (60).
 *
 * An overlay joins with its open state and its close function, in the component
 * that owns it. The registry holds the open ones; becoming open closes the rest,
 * and leaving (closing, unmounting) removes the entry. The close function is
 * read through a ref, so an inline `() => setOpen(false)` is fine.
 *
 * ADR-106 P3's `useSwipeNavBlock` answers a different question (is anything open?);
 * this one answers which may stay open.
 */
const open = new Map<symbol, () => void>()

export function useExclusiveOverlay(active: boolean, close: () => void): void {
  const closeRef = useRef(close)
  useLayoutEffect(() => {
    closeRef.current = close
  })

  useLayoutEffect(() => {
    if (!active) return
    for (const closeOther of [...open.values()]) closeOther()
    const id = Symbol('overlay')
    open.set(id, () => closeRef.current())
    return () => {
      open.delete(id)
    }
  }, [active])
}

/** Overlays currently open (for tests). */
export function openOverlayCount(): number {
  return open.size
}
